import { getCurrentPath } from "@faire/web--source/common/Path";
import { Button } from "@faire/web--source/slate/Button";
import { Lock } from "@faire/web--source/slate/icons/foundation/Lock";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCxFoundationsContinueWithoutSignInClick } from "@faire/web-api--source/events/cxFoundations/click/continueWithoutSignIn";
import { trackCxFoundationsSignInClick } from "@faire/web-api--source/events/cxFoundations/click/signIn";
import { trackCxFoundationsSignInModalCloseClick } from "@faire/web-api--source/events/cxFoundations/click/signInModalClose";
import privacy from "@faire/web-api--source/routes/privacy";
import homepage from "@faire/web-api--source/routes/root";
import { route as supportHomeRoute } from "@faire/web-api--source/routes/support";
import { route as supportSelfServeTopicsRoute } from "@faire/web-api--source/routes/support/self-serve/topics";
import { History } from "history";
import omit from "lodash/omit";
import qs from "query-string";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";

export const LoginModal: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const locationSearch = (): qs.ParsedQuery => {
    return qs.parse(location?.search ?? "");
  };

  const shouldGoBackToHome = (): boolean => {
    return !!locationSearch().signIn;
  };

  const redirectToPath = () => {
    if (shouldGoBackToHome()) {
      return supportHomeRoute;
    }
    return supportSelfServeTopicsRoute;
  };

  const closeLoginModal = () => {
    removeContactQueryParam(history);
    trackCxFoundationsSignInModalCloseClick();
  };

  return (
    <Modal
      onClose={closeLoginModal}
      justify="center"
      height="auto"
      width="400px"
      padding="64px 24px 24px"
      shouldCloseOnOverlayClick={false}
    >
      <Column>
        <Typography variant="pageHeaderSerifRegular">
          <StrictLocalMsg
            defaultMessage="Sign in to contact us"
            description={{
              text: "Ask customer to login their Faire account",
              img: "https://cdn.faire.com/fastly/5bee09a70428d34e8e3627bc9214e70ba81e0cd5d1b54b458cac51bcae1a1e44.png",
            }}
          />
        </Typography>
        <Spacer height="4x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="Connect your account so we can better help you find what you’re looking for."
            description={{
              img: "https://cdn.faire.com/fastly/5bee09a70428d34e8e3627bc9214e70ba81e0cd5d1b54b458cac51bcae1a1e44.png",
            }}
          />
        </Typography>
        <Spacer height="4x" />
        <Button
          linkTo={homepage({
            signIn: "1",
            redirectTo: redirectToPath(),
          })}
          onClick={trackCxFoundationsSignInClick}
          data-test-id="login-modal-sign-in-button"
        >
          <StrictLocalMsg
            defaultMessage="Sign in"
            description={{
              text: "CTA button to login with Faire account",
              img: "https://cdn.faire.com/fastly/5bee09a70428d34e8e3627bc9214e70ba81e0cd5d1b54b458cac51bcae1a1e44.png",
            }}
          />
        </Button>
        <Spacer height="2x" />
        <Button
          variant="tertiary"
          linkTo={getCurrentPath().append({ loggedOut: "true" }).toString()}
          onClick={trackCxFoundationsContinueWithoutSignInClick}
          data-test-id="continue-without-signing-in-button"
        >
          <StrictLocalMsg
            defaultMessage="Continue without signing in"
            description={{
              text: "CTA button to continue without login",
              img: "https://cdn.faire.com/fastly/5bee09a70428d34e8e3627bc9214e70ba81e0cd5d1b54b458cac51bcae1a1e44.png",
            }}
          />
        </Button>
        <Spacer height="2x" />
        <Row align="center">
          <Lock fontSize="16" lockType="roundedSquare" />
          <Spacer size="1x" />
          <Typography>
            <StrictLocalMsg
              defaultMessage="Learn more about how we use and protect your data at Faire. <LinkFormatting>Privacy at Faire</LinkFormatting>"
              values={{
                LinkFormatting: LinkToPrivacy,
              }}
              description={{
                img: "https://cdn.faire.com/fastly/5bee09a70428d34e8e3627bc9214e70ba81e0cd5d1b54b458cac51bcae1a1e44.png",
              }}
            />
          </Typography>
        </Row>
      </Column>
    </Modal>
  );
};

const LinkToPrivacy = (chunks: React.ReactNode) => (
  <Button variant="tertiary" linkTo={privacy()}>
    {chunks}
  </Button>
);

const removeContactQueryParam = (history: History) => {
  const pathname = history.location.pathname;
  const search = history.location.search ?? "";
  const query = qs.parse(search);
  history.push(`${pathname}?${qs.stringify(omit(query, ["contact"]))}`);
};
